// @react
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
// @libraries
import { DevTool } from '@hookform/devtools'
import { makeStyles } from '@material-ui/core/styles'
// @common
import strings from 'lib/constants/strings'
// @types
import {
  GetConstantsQueryResult,
  GetUserQueryResult,
} from 'lib/graphql/graphqlTypes'
// @components
import Container from '@bit/atd.web.container'
import Grid from '@bit/atd.web.grid'
import Spacer from '@bit/atd.web.spacer'
import Box from '@bit/atd.web.box'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import WorkPublicFields from 'modules/common/forms/fields/work/WorkPublic'
import WorkHiddenFields from 'modules/common/forms/fields/work/WorkHidden'
import Navigation from 'modules/onBoarding/components/_common/Navigation'
import { Slide } from '@material-ui/core'
import { Direction, NavigationContext } from 'modules/common/context/navigation'
import useWorkFormWrapper from 'modules/common/forms/hooks/useWorkFormWrapper'
import useNavigation from 'modules/onBoarding/hooks/useNavigation'
import { WorkFormValues } from 'modules/common/forms/types/work'

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '768px',
    margin: '0 auto',
    '& .MuiPaper-root': {
      width: '100%',
    },
  },
  formContainer: {
    position: 'relative',
  },
}))

type PropsType = {
  data?: GetUserQueryResult['data']
  constants?: GetConstantsQueryResult['data']
  isLoading: boolean
  refetch: any
}

const Work = ({
  data: userData,
  constants: constantData,
  refetch,
  isLoading,
}: PropsType): ReactElement => {
  /******************************************
   * STATE
   ******************************************/

  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [direction, setDirection] = useState<Direction>(Direction.LEFT)
  const [slidingIn, setSlidingIn] = useState<boolean>(false)
  const [destination, setDestination] = useState<string>('')

  /******************************************
   * HOOKS
   ******************************************/
  const classes = useStyles()
  const history = useHistory()
  const navigation = useContext(NavigationContext)
  const { formMethods, control, userJobUpdateHandler } = useWorkFormWrapper({
    data: userData,
    isLoading,
    refetch,
  })

  // React Hook Form
  const {
    formState: { dirtyFields, errors },
  } = formMethods
  const isFormDirty = Object.keys(dirtyFields).length > 0

  const { handleNextClick, handlePrevClick, handleSaveExit } = useNavigation({
    setDestination,
    setDirection,
    setSlidingIn,
    setIsSubmitting,
    isFormDirty,
    updateHandler: userJobUpdateHandler,
  })

  useEffect(() => {
    setSlidingIn(true)
  }, [])

  /******************************************
   * RENDER
   ******************************************/

  const box = (content: ReactElement, isLoading: boolean) => {
    return <Box>{!isLoading ? content : <SkeletonLoader />}</Box>
  }

  return (
    <>
      {process.env.DEBUG !== 'true' && <DevTool control={control} />}
      <Spacer size={32} fullWidth />

      {/* Form */}
      <form className={classes.formContainer}>
        <Slide
          in={slidingIn}
          timeout={200}
          direction={navigation.direction}
          unmountOnExit
          onExited={() => {
            navigation.setDirection(direction)
            history.push(destination)
          }}
        >
          <div>
            <Container>
              <Grid className={classes.container} container>
                {box(
                  <>
                    <Grid container spacing={4}>
                      <WorkPublicFields
                        editing
                        userData={userData}
                        constantData={constantData}
                        control={control}
                        errors={errors}
                      />

                      <WorkHiddenFields
                        editing
                        constantData={constantData}
                        control={control}
                      />
                    </Grid>
                  </>,
                  isLoading,
                )}
              </Grid>
            </Container>
          </div>
        </Slide>

        <Navigation
          handleExitOnClick={formMethods.handleSubmit(
            (data: WorkFormValues) => handleSaveExit(data),
            (errors) => handleSaveExit(undefined, errors),
          )}
          handlePrevOnClick={formMethods.handleSubmit(
            (data: WorkFormValues) => handlePrevClick(data),
            (errors) => handlePrevClick(undefined, errors),
          )}
          handleNextOnClick={formMethods.handleSubmit(
            (data: WorkFormValues) => handleNextClick(data),
            (errors) => handleNextClick(undefined, errors),
          )}
          prevPage={strings.ONBOARDING.PROFILE.HEADER.TITLE}
          nextPage={strings.ONBOARDING.EDUCATION.HEADER.TITLE}
          isSubmitting={isMutationSubmitting}
          prevDisable={isLoading || isMutationSubmitting}
          exitDisable={isLoading || isMutationSubmitting}
          nextDisable={isLoading || isMutationSubmitting}
        />
      </form>
    </>
  )
}

export default Work
